import React, { useState, useEffect } from "react";
import useCustomMutation from "../../Hooks/useCustomMutation";
import useCustomFecthQuery from "../../Hooks/useCustomFecthQuery";
import { Row, Col, Spinner } from "reactstrap";
import ButtonSubmit from "../Buttons/ButtonSubmit";
import { dangerMessage } from "../../components/Toast/Toast";

const EditFaq = ({ eventID, faqID, token, toggleEditFAQ }) => {
  // Invalidate the same query that loads eventDetails in EventSetUp
  // EventSetUp uses key ["myEventDetail", eventID]
  const key = ["myEventDetail", eventID];
  const keyFaq = ["faqDetails", faqID];
  const [inputs, setInputs] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const mutation = useCustomMutation(key);
  const deleteMutation = useCustomMutation(key);

  const {
    data: getFaq,
    error: errorGetFaq,
    isLoading: isLoadingGetFaq,
  } = useCustomFecthQuery(
    keyFaq,
    `${process.env.REACT_APP_API}faq/${faqID}`,
    token,
    { enabled: !!faqID }
  );

  // Debug: Log props and data
  useEffect(() => {
    console.log("EditFaq component mounted/updated:", {
      faqID,
      eventID,
      getFaq: getFaq,
      getFaqData: getFaq?.data,
      inputs: inputs,
      hasToken: !!token
    });
  }, [faqID, eventID, getFaq, inputs, token]);

  // console.log("....getFaq....", getFaq);
  // console.log("....faqDetails....", faqDetails);
  // console.log("....inputs....faq....", faqDetails);

  const handleEditFaq = (event) => {
    event.preventDefault();

    // Use the same robust FAQ ID resolution as delete
    const actualFaqID =
      getFaq?.data?._id ||
      inputs?._id ||
      getFaq?.data?.id ||
      inputs?.id ||
      faqID;

    if (!actualFaqID) {
      dangerMessage("FAQ ID is missing. Cannot update. Please try refreshing the page.");
      console.error("EditFaq: FAQ ID is missing on update", {
        faqID,
        getFaq,
        getFaqData: getFaq?.data,
        inputs,
        actualFaqID,
        eventID,
        token,
      });
      return;
    }

    const Method = "PATCH";
    const url = `faq/${actualFaqID}?eid=${eventID}`;
    const isJSON = true;

    const raw = JSON.stringify({
      question: inputs?.question,
      answer: inputs?.answer,
    });

    const data = {
      endPoint: url,
      bodyContent: raw,
      Method: Method,
      displayMessage: "FAQ successfully updated",
      token: token,
      isJSON: isJSON,
    };

    console.log("EditFaq: Attempting to update FAQ", {
      faqID: actualFaqID,
      eventID,
      url,
      payload: raw,
    });

    mutation.mutate(data);
  };

  useEffect(() => {
    if (mutation?.isSuccess) {
      toggleEditFAQ();
    }
    if (getFaq?.status === "ok" || getFaq?.data) {
      setInputs(getFaq?.data || getFaq);
    }
  }, [mutation?.isSuccess, getFaq?.status, getFaq?.data]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleDeleteFaq = () => {
    // Try multiple sources for FAQ ID (fetched data, inputs, or prop)
    const actualFaqID = getFaq?.data?._id || 
                        inputs?._id || 
                        getFaq?.data?.id || 
                        inputs?.id || 
                        faqID;
    
    // Validate required fields
    if (!actualFaqID) {
      dangerMessage("FAQ ID is missing. Cannot delete. Please try refreshing the page.");
      console.error("EditFaq: FAQ ID is missing", { 
        faqID, 
        getFaq: getFaq,
        getFaqData: getFaq?.data,
        inputs: inputs,
        actualFaqID, 
        eventID, 
        token 
      });
      return;
    }

    if (!token) {
      dangerMessage("Authentication token is missing. Please log in again.");
      console.error("EditFaq: token is missing", { faqID: actualFaqID, eventID });
      return;
    }

    if (!eventID) {
      dangerMessage("Event ID is missing. Cannot delete FAQ.");
      console.error("EditFaq: eventID is missing", { faqID: actualFaqID, eventID, token });
      return;
    }

    console.log("EditFaq: Attempting to delete FAQ", { 
      faqID: actualFaqID, 
      eventID, 
      url: `faq/${actualFaqID}?eid=${eventID}` 
    });

    const Method = "DELETE";
    const url = `faq/${actualFaqID}?eid=${eventID}`;
    const data = {
      endPoint: url,
      bodyContent: null,
      Method: Method,
      displayMessage: "FAQ successfully deleted",
      token: token,
      isJSON: null,
    };
    
    deleteMutation.mutate(data);
  };

  // Handle delete success - close the edit form
  useEffect(() => {
    if (deleteMutation?.isSuccess) {
      toggleEditFAQ();
    }
  }, [deleteMutation?.isSuccess, toggleEditFAQ]);

  // Handle delete error
  useEffect(() => {
    if (deleteMutation?.isError) {
      console.error("EditFaq: Delete error", {
        error: deleteMutation.error,
        faqID,
        eventID,
        mutationData: deleteMutation.data
      });
      const errorMessage = deleteMutation?.error?.response?.data?.message || 
                          deleteMutation?.error?.message || 
                          "Failed to delete FAQ. Please try again.";
      dangerMessage(errorMessage);
      setShowDeleteConfirm(false);
    }
  }, [deleteMutation?.isError, deleteMutation?.error, faqID, eventID]);

  return (
    <div>
      <form
        noValidate
        autoComplete="off"
        data-testid="form"
        onSubmit={handleEditFaq}
      >
        <Row>
          <Col md={12} lg={12}>
            <label>
              <span className="text-danger">*</span>Question
            </label>

            <textarea
              name="question"
              value={inputs?.question}
              onChange={handleChange}
              placeholder="type your question here..."
              className="mb-3 form-control fromInput"
              rows={3}
              required
            ></textarea>
          </Col>
          <Col md={12} lg={12}>
            <label>
              <span className="text-danger">*</span>Answer
            </label>
            <textarea
              name="answer"
              value={inputs?.answer}
              onChange={handleChange}
              placeholder="Write here..."
              className="mb-3 form-control fromInput"
              rows={4}
              required
            ></textarea>
          </Col>
          
          {showDeleteConfirm ? (
            <Col md={12} lg={12}>
              <div className="alert alert-warning mb-3" role="alert">
                <h6 className="text-danger mb-2">
                  Are you sure you want to delete this FAQ?
                </h6>
                <p className="mb-0 small">This action cannot be undone.</p>
                {deleteMutation?.isError && (
                  <div className="alert alert-danger mt-2 mb-0" role="alert">
                    <strong>Error:</strong> {deleteMutation?.error?.response?.data?.message || deleteMutation?.error?.message || "An error occurred while deleting the FAQ."}
                  </div>
                )}
                <div className="d-flex gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleDeleteFaq}
                    disabled={
                      deleteMutation?.isLoading || 
                      !(getFaq?.data?._id || getFaq?.data?.id || inputs?._id || inputs?.id || faqID) || 
                      !token || 
                      !eventID ||
                      isLoadingGetFaq
                    }
                  >
                    {!deleteMutation?.isLoading ? (
                      "Yes, Delete"
                    ) : (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Deleting...
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteMutation?.isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Col>
          ) : (
            <Col md={12} lg={12}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => {
                    const checkFaqID = getFaq?.data?._id || getFaq?.data?.id || inputs?._id || inputs?.id || faqID;
                    if (!checkFaqID) {
                      dangerMessage("FAQ data not loaded yet. Please wait a moment and try again.");
                      console.error("Delete button clicked but FAQ ID is missing:", {
                        faqID,
                        getFaq: getFaq,
                        inputs: inputs
                      });
                      return;
                    }
                    setShowDeleteConfirm(true);
                  }}
                  disabled={
                    mutation?.isLoading || 
                    deleteMutation?.isLoading || 
                    isLoadingGetFaq ||
                    !(getFaq?.data?._id || getFaq?.data?.id || inputs?._id || inputs?.id || faqID)
                  }
                >
                  Delete FAQ
                </button>
              </div>
            </Col>
          )}
          
          <div className="w-100 MT-2 d-flex justify-content-center">
            <ButtonSubmit
              Title="Update FAQ"
              ColorText="white"
              BorderColor="#F68A20"
              BackgroundColor="#F68A20"
              borderRadius="25px"
              handleOnclick={handleEditFaq}
              pending={mutation?.isLoading}
            />
          </div>
        </Row>
      </form>
    </div>
  );
};

export default EditFaq;
